import React, { useCallback, useRef, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import {
  projectV2ProjectViewModeAtom,
  projectV2ShowCompletedTasksAtom
} from '../../../../atoms/v3/projectV2Atoms';
import { CompletionControlPanel } from './CompletionControlPanel';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
import { layoutModeAtom } from '../../../../atoms/v3/uiAtoms';
import { IOSHeaderContainer } from '../ios/IOSHeaderContainer';

/**
 * ProjectV2 页面头部组件
 * 参考 TaskV2Header 的设计风格
 */
export const ProjectV2Header: React.FC = () => {
  const { t } = useTranslation(['v3/projects', 'v3/tasks']);
  const [isDark] = useAtom(isDarkAtom);
  const layoutMode = useAtomValue(layoutModeAtom);

  // 状态管理
  const [projectViewMode, setProjectViewMode] = useAtom(projectV2ProjectViewModeAtom);
  const [showCompletedTasks, setShowCompletedTasks] = useAtom(projectV2ShowCompletedTasksAtom);

  // 控制面板状态
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const eyeButtonRef = useRef<HTMLButtonElement>(null);

  // 项目视图模式切换
  const handleProjectViewModeChange = useCallback(() => {
    const modes: ('compact' | 'expanded' | 'card' | 'tabs')[] = ['compact', 'expanded', 'card', 'tabs'];
    const currentIndex = modes.indexOf(projectViewMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    setProjectViewMode(modes[nextIndex]);
  }, [projectViewMode, setProjectViewMode]);

  // 控制面板切换
  const handlePanelToggle = useCallback(() => {
    setIsPanelOpen(!isPanelOpen);
  }, [isPanelOpen]);

  // 关闭控制面板
  const handlePanelClose = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  // 判断是否应该显示控制面板按钮 - 所有项目模式都显示
  const shouldShowControlPanel = true;

  // 判断是否应该显示已完成任务开关 - 只有包含任务的模式才显示
  const shouldShowTasksToggle = ['expanded', 'card', 'tabs'].includes(projectViewMode);

  // 获取当前视图模式的显示文本
  const getCurrentViewModeText = () => {
    switch (projectViewMode) {
      case 'compact':
        return t('v3/projects:viewMode.compact');
      case 'expanded':
        return t('v3/projects:viewMode.expanded');
      case 'card':
        return t('v3/projects:viewMode.card');
      case 'tabs':
        return t('v3/projects:viewMode.tabs');
      default:
        return '';
    }
  };

  // 获取当前视图模式的图标
  const getCurrentViewModeIcon = () => {
    switch (projectViewMode) {
      case 'compact':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        );
      case 'expanded':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
        );
      case 'card':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
          </svg>
        );
      case 'tabs':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <IOSHeaderContainer mode={layoutMode === 'desktop' ? 'desktop-main' : 'mobile'}>
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          {/* 左侧：页面标题 */}
          <h1 className={`
            text-lg font-semibold
            ${isDark ? 'text-ios-dark-gray1' : 'text-gray-900'}
          `}>
            {t('v3/projects:tabs.project')}
          </h1>



          {/* 右侧：控制区域 */}
          <div className="flex items-center gap-2">
            {/* 控制面板按钮 - 只在特定模式下显示 */}
            {shouldShowControlPanel && (
              <div className="relative">
                <button
                  ref={eyeButtonRef}
                  onClick={handlePanelToggle}
                  className={`
                    flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200
                    ${isPanelOpen
                      ? isDark
                        ? 'bg-blue-900/30 text-blue-400'
                        : 'bg-blue-100 text-blue-600'
                      : isDark
                        ? 'hover:bg-ios-dark-gray4 text-ios-dark-gray1'
                        : 'hover:bg-gray-100 text-gray-600'
                    }
                  `}
                  title="显示选项"
                >
                  {/* 眼睛图标 */}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>

                {/* 控制面板 */}
                <CompletionControlPanel
                  isOpen={isPanelOpen}
                  onClose={handlePanelClose}
                  anchorRef={eyeButtonRef}
                  showTasksToggle={shouldShowTasksToggle}
                />
              </div>
            )}

            <button
              onClick={handleProjectViewModeChange}
              className={`
                flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-200
                ${isDark
                  ? 'bg-ios-dark-gray5 text-ios-dark-gray2 hover:bg-ios-dark-gray4'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }
              `}
            >
              {getCurrentViewModeIcon()}
              <span className="text-sm font-medium">
                {getCurrentViewModeText()}
              </span>
            </button>
          </div>
        </div>
      </div>
    </IOSHeaderContainer>
  );
};
